import { productLanguage, shopifyComponentCopy } from '@testfeed/product-language'
import { BlockStack } from '@shopify/polaris'
import { useCallback } from 'react'
import { PreparedSetupsPanel } from './PreparedSetupsPanel'
import { useStudySetupsPageController } from './study-setups-page-controller'
import { HouseEmptyState } from './imagery/HouseEmptyState'
import { houseImagery } from './imagery/houseImagery'
import type { SavedStudyBuilderDraftSummary } from '../hooks/study-builder/studyBuilderDrafts'
import type { ReactNode } from 'react'
import { canSelectStudyForDeletion } from './study-deletion-eligibility'

export type StudySetupsConnectionState = 'connected' | 'disconnected' | 'needs-connection' | 'failed'

type StudySetupsPageProps = {
  isLoading?: boolean
  onCreateStudy?: () => void
  savedDrafts?: SavedStudyBuilderDraftSummary[]
  isLoadingSavedDrafts?: boolean
  savedDraftsError?: unknown
  onRetrySavedDrafts?: () => void
  onResumeDraft?: (draftId: string, studyId?: string, status?: string, runId?: string | null) => void
  state?: StudySetupsConnectionState
  settingsUrl?: string
  isAuthoringAvailable?: boolean
  renderDraftActions?: (draft: SavedStudyBuilderDraftSummary) => ReactNode
  selection?: { selectedIds: string[]; onChange: (ids: string[]) => void; onDelete?: () => void }
}

const copy = productLanguage.shopify.studyBuilder

export function StudySetupsPage({
  isLoading = false,
  onCreateStudy,
  savedDrafts = [],
  isLoadingSavedDrafts = false,
  savedDraftsError = null,
  onRetrySavedDrafts,
  onResumeDraft,
  state = 'connected',
  settingsUrl,
  isAuthoringAvailable = true,
  renderDraftActions,
  selection,
}: StudySetupsPageProps) {
  const clearSelection = useCallback(() => selection?.onChange([]), [selection?.onChange])
  const controller = useStudySetupsPageController({
    savedDrafts,
    onSelectionContextChange: clearSelection,
  })
  const settingsAction = settingsUrl
    ? { content: shopifyComponentCopy.getStartedCard.connectStepAction, url: settingsUrl }
    : undefined

  if (state === 'needs-connection') {
    return (
      <s-section>
        <HouseEmptyState
          heading={copy.readinessBanners.connectionSelectionRequired.title}
          image={houseImagery.capabilityPending}
          action={settingsAction}
        >
          <p>{copy.readinessBanners.connectionSelectionRequired.body}</p>
        </HouseEmptyState>
      </s-section>
    )
  }

  if (state === 'failed') {
    return (
      <s-section>
        <HouseEmptyState heading="TestFeed connection could not be checked" image={houseImagery.capabilityPending}>
          <p>Reload the page and try again.</p>
        </HouseEmptyState>
      </s-section>
    )
  }

  if (state === 'disconnected') {
    return (
      <s-section>
        <HouseEmptyState
          heading={copy.readinessBanners.disconnected.title}
          image={houseImagery.capabilityPending}
          action={settingsAction}
        >
          <p>{copy.readinessBanners.disconnected.body}</p>
        </HouseEmptyState>
      </s-section>
    )
  }

  return (
    <BlockStack gap="400">
      <PreparedSetupsPanel
        rows={controller.pagedRows}
        onResumeDraft={onResumeDraft}
        renderDraftActions={renderDraftActions}
        selection={
          selection
            ? {
                selectedIds: selection.selectedIds,
                onChange: selection.onChange,
                getSelectable: (row) => canSelectStudyForDeletion(row.draft),
                getLabel: (row) => row.draft.title,
              }
            : undefined
        }
        onDeleteSelected={selection?.onDelete}
        isLoading={isLoading || isLoadingSavedDrafts}
        onCreateStudy={isAuthoringAvailable ? onCreateStudy : undefined}
        onClearFilters={controller.clearFilters}
        hasAnySetups={savedDrafts.length > 0 || Boolean(savedDraftsError)}
        hasLoadedRows={savedDrafts.length > 0}
        views={controller.views}
        selectedView={controller.selectedQueue}
        onSelectView={(id) =>
          controller.commitState({
            queue: controller.views.some((view) => view.id === id) ? (id as typeof controller.selectedQueue) : 'all',
            page: 1,
          })
        }
        queryValue={controller.queryValue}
        onQueryChange={(value) => controller.commitState({ query: value, page: 1 })}
        sortSelected={[controller.sort.replace(/-(asc|desc)$/, ' $1')]}
        onSort={controller.selectSort}
        filters={controller.studyFilters}
        appliedFilters={controller.appliedFilters}
        draftsError={savedDraftsError}
        onRetryDrafts={onRetrySavedDrafts}
        page={controller.visiblePage}
        pages={controller.pageCount}
        onPageChange={(page) => controller.commitState({ page })}
      />
    </BlockStack>
  )
}
